import i18next from 'i18next';
import { httpClient } from '@/api/httpClient';
import { SUPPORTED_LANGUAGE } from '@/i18n/supportedLanguages';

export interface PatientOptionItem {
  code: string;
  labelEn: string;
  labelPl: string;
  imageUrl: string | null;
}

export interface PatientOptions {
  species: PatientOptionItem[];
  warnings: PatientOptionItem[];
}

interface PatientOptionItemApiResource {
  code: string;
  label_en: string;
  label_pl: string;
  image_url: string | null;
}

interface PatientOptionsApiResource {
  species: PatientOptionItemApiResource[];
  warnings: PatientOptionItemApiResource[];
}

export interface PatientOptionDraft {
  code: string | null;
  labelEn: string;
  labelPl: string;
}

function mapItem(resource: PatientOptionItemApiResource): PatientOptionItem {
  return {
    code: resource.code,
    labelEn: resource.label_en,
    labelPl: resource.label_pl,
    imageUrl: resource.image_url,
  };
}

function mapOptions(data: PatientOptionsApiResource): PatientOptions {
  return { species: data.species.map(mapItem), warnings: data.warnings.map(mapItem) };
}

export function resolvePatientOptionLabel(item: PatientOptionItem): string {
  return i18next.language === SUPPORTED_LANGUAGE.polish ? item.labelPl : item.labelEn;
}

export function resolveSpeciesLabel(speciesOptions: PatientOptionItem[], code: string): string {
  const matchingOption = speciesOptions.find(function hasCode(option) {
    return option.code === code;
  });
  return matchingOption === undefined ? code : resolvePatientOptionLabel(matchingOption);
}

export async function fetchOwnPetSpeciesOptions(): Promise<PatientOptionItem[]> {
  const response = await httpClient.get<{ species: PatientOptionItemApiResource[] }>(
    '/me/pet-options'
  );
  return response.data.species.map(mapItem);
}

export async function fetchWorkspacePatientOptions(): Promise<PatientOptions> {
  const response = await httpClient.get<PatientOptionsApiResource>('/me/workspace/patient-options');
  return mapOptions(response.data);
}

export async function setWorkspacePatientOptions(
  category: 'species' | 'warning',
  items: PatientOptionDraft[]
): Promise<PatientOptions> {
  const response = await httpClient.put<PatientOptionsApiResource>(
    '/me/workspace/patient-options/' + category,
    {
      options: items.map(function toApi(item) {
        return { code: item.code, label_en: item.labelEn, label_pl: item.labelPl };
      }),
    }
  );
  return mapOptions(response.data);
}

export async function uploadPatientOptionImage(
  category: 'species' | 'warning',
  code: string,
  file: File
): Promise<PatientOptions> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await httpClient.post<PatientOptionsApiResource>(
    '/me/workspace/patient-options/' + category + '/' + code + '/image',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return mapOptions(response.data);
}

export async function removePatientOptionImage(
  category: 'species' | 'warning',
  code: string
): Promise<PatientOptions> {
  const response = await httpClient.delete<PatientOptionsApiResource>(
    '/me/workspace/patient-options/' + category + '/' + code + '/image'
  );
  return mapOptions(response.data);
}
